import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ListCampaigns.scss";
import { FaWhatsapp, FaChevronDown, FaTimes } from "react-icons/fa";
import { BsTelephone, BsThreeDotsVertical } from "react-icons/bs";
import { HiAdjustments } from "react-icons/hi";
import { BiSearch } from "react-icons/bi";
import { Form, Dropdown, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faCalendar,
  faChevronDown,
  faTimes,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import ConfirmacionModal from "../../../../components/form-components/confirmacion-modal.jsx";
import { FiCopy, FiEdit2, FiTrash2 } from "react-icons/fi";
import { RiMore2Fill, RiFileCopyLine, RiPencilLine, RiDeleteBinLine, RiSendPlaneLine, RiEyeLine } from "react-icons/ri";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from "@mui/material";
import { campaignService } from "../../../../services/campaignService";
import { format } from "date-fns";

// Registrar el locale español
registerLocale("es", es);

const temperatureOptions = [
  { value: "Frío", label: "Frío" },
  { value: "Tibio", label: "Tibio" },
  { value: "Caliente", label: "Caliente" },
];

export const ListCampaigns = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(5);
  const [showFilters, setShowFilters] = useState(false);
  const [showTemperatureDropdown, setShowTemperatureDropdown] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: {
      start: new Date("2024-11-15"),
      end: new Date("2024-12-04"),
    },
    temperature: ["Frío", "Tibio", "Caliente"],
  });
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemperatures, setSelectedTemperatures] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showPlantillaModal, setShowPlantillaModal] = useState(false);
  const [selectedPlantillaDetails, setSelectedPlantillaDetails] = useState(null);

  // Calculate total pages
  const totalPages = Math.ceil(campaigns.length / recordsPerPage);

  // Get current page data
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return campaigns.slice(startIndex, endIndex);
  };

  const handleToggleActive = async (campaignId) => {
    // Encontrar la campaña actual para obtener su estado
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (!campaign) return;

    // ✅ Envía los textos exactos que tu Base de Datos entiende
    const nuevoEstado = campaign.active ? 'deshabilitado' : 'habilitado';

    // Actualizar el estado localmente primero (optimistic update)
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((c) =>
        c.id === campaignId
          ? { ...c, active: !c.active }
          : c
      )
    );

    try {
      // Llamar al API para actualizar el estado
      await campaignService.cambiarEstadoCampaign(campaignId, nuevoEstado);
    } catch (error) {
      console.error('Error al cambiar estado de la campaña:', error);
      // Revertir el cambio si falla
      setCampaigns((prevCampaigns) =>
        prevCampaigns.map((c) =>
          c.id === campaignId
            ? { ...c, active: campaign.active }
            : c
        )
      );
      // Mostrar mensaje de error al usuario
      alert(error.message || 'Error al cambiar el estado de la campaña. Por favor intenta nuevamente.');
    }
  };

  const handleAddTemperature = (temperature) => {
    if (!selectedTemperatures.includes(temperature)) {
      setSelectedTemperatures([...selectedTemperatures, temperature]);
    }
    setShowTemperatureDropdown(false);
  };

  const handleRemoveTemperature = (temperature) => {
    setSelectedTemperatures(
      selectedTemperatures.filter((temp) => temp !== temperature)
    );
  };

  const handleDateChange = (date, type) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: date,
      },
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy", { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  // Mapear datos del API a formato de la tabla
  const mapCampaignData = (apiData) => {
    return apiData.map((campaign) => {
      // Determinar canales (Ahora solo WhatsApp por defecto)
      const channels = ["whatsapp"];

      // Obtener el estado de la plantilla si existe
      let plantillaEstado;
      let plantillaMetaStatus = null;
      if (campaign.plantilla) {
        const metaStatus = campaign.plantilla.metaStatus || 'LOCAL';
        plantillaMetaStatus = metaStatus;
        const statusLabels = {
          'LOCAL': 'Local',
          'PENDING': 'Pendiente',
          'APPROVED': 'Aprobada',
          'REJECTED': 'Rechazada'
        };
        plantillaEstado = statusLabels[metaStatus] || metaStatus;
      } else {
        plantillaEstado = 'Sin plantilla';
      }

      // Obtener estado de la campaña
      const estadoLabels = {
        'borrador': 'Borrador',
        'programado': 'Programado',
        'procesando': 'Procesando',
        'completado': 'Completado',
        'cancelado': 'Cancelado',
        'pausado': 'Pausado',
        'fallido': 'Fallido'
      };
      const estadoCampania = estadoLabels[campaign.estado] || campaign.estado;

      // Obtener estadísticas
      const stats = campaign.stats || {};
      const enviados = stats.enviados || 0;
      const total = stats.total || 0;
      const contactados = `${enviados}/${total}`;

      return {
        id: campaign.id,
        name: campaign.nombre,
        date: formatDate(campaign.createdAt),
        channels: channels,
        plantillaEstado: plantillaEstado,
        plantillaMetaStatus: plantillaMetaStatus,
        plantillaData: campaign.plantilla,
        estadoCampania: estadoCampania,
        estadoCampaniaRaw: campaign.estado,
        contactados: contactados,
        active: campaign.estado === "programado" || campaign.estado === "procesando",
        originalData: campaign,
      };
    });
  };

  // Cargar campañas del API
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await campaignService.listarCampaigns();

        if (response.success && response.data) {
          const mappedCampaigns = mapCampaignData(response.data);
          setCampaigns(mappedCampaigns);
        } else {
          setError("No se pudieron cargar las campañas");
        }
      } catch (err) {
        console.error("Error al cargar campañas:", err);
        setError(err.message || "Error al cargar las campañas");
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, []);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      temperature: selectedTemperatures,
    }));
  }, [selectedTemperatures]);

  // Resetear a página 1 cuando cambien los registros por página
  useEffect(() => {
    setCurrentPage(1);
  }, [recordsPerPage]);

  const handleDelete = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!campaignToDelete) return;

    try {
      setDeleting(true);
      // Llamar al API para eliminar la campaña
      await campaignService.eliminarCampaign(campaignToDelete.id);

      // Si la eliminación fue exitosa, actualizar el estado local
      setCampaigns((prev) => prev.filter((c) => c.id !== campaignToDelete.id));
      setShowDeleteModal(false);
      setCampaignToDelete(null);
    } catch (err) {
      console.error('Error al eliminar campaña:', err);
      // Mostrar mensaje de error al usuario
      alert(err.message || 'Error al eliminar la campaña. Por favor intenta nuevamente.');
    } finally {
      setDeleting(false);
    }
  };
  // --- NUEVA FUNCIÓN PARA EJECUTAR CAMPAÑA ---
  const handleExecute = async (campaign) => {
    // 1. Confirmación de seguridad
    if (!window.confirm(`¿Estás seguro de iniciar el envío masivo de la campaña "${campaign.name}"?`)) {
      return;
    }

    try {
      // 2. Llamada al servicio (Asegúrate de haber actualizado campaignService.js antes)
      await campaignService.ejecutarCampaign(campaign.id);

      // 3. Feedback al usuario
      alert(`🚀 ¡Campaña "${campaign.name}" iniciada! Los mensajes se enviarán en segundo plano.`);

      // Opcional: Recargar la lista para actualizar estados
      // window.location.reload(); 
    } catch (err) {
      console.error('Error al ejecutar campaña:', err);
      alert('❌ Error: ' + (err.message || 'No se pudo iniciar la campaña'));
    }
  };

  const baseUrl = import.meta.env.BASE_URL || "/";
  const withBase = (path) => `${baseUrl}${path}`.replace(/\/{2,}/g, "/");

  return (
    <Box sx={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
      <Box className="breadcrumb">
        <span>Automatización</span> <span className="chevron">›</span>{" "}
        <span className="current">Campañas</span>
      </Box>
      <div className="campaigns-container">
        <div className="campaigns-header">
          <h1>Campañas</h1>
          <button
            className="new-campaign-btn"
            onClick={() => navigate(withBase("dashboard/automatizacion/campaigns/new"))}
          >
            Nueva campaña
          </button>
        </div>

        <div className="campaigns-content">
          <h2>Registro de campañas</h2>
          <p className="description">
            En esta sección podrás visualizar, editar, activar o
            desactivar tus campañas.
          </p>

          <div className="campaigns-tools">
            <div className="filters-section">
              <button
                className="filter-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <HiAdjustments />
                Filtros
              </button>

              {showFilters && (
                <div className="filters-dropdown">
                  <div className="filter-row">
                    <div className="filter-field">
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="light"
                          className="filter-dropdown-toggle"
                        >
                          Fecha <FontAwesomeIcon icon={faChevronDown} />
                        </Dropdown.Toggle>
                      </Dropdown>
                      <div className="date-range">
                        <span>Del</span>
                        <div className="date-input">
                          <DatePicker
                            selected={filters.dateRange.start}
                            onChange={(date) => handleDateChange(date, "start")}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            locale="es"
                            selectsStart
                            startDate={filters.dateRange.start}
                            endDate={filters.dateRange.end}
                            placeholderText="Seleccionar fecha"
                          />
                          <FontAwesomeIcon icon={faCalendar} />
                        </div>
                        <span>Al</span>
                        <div className="date-input">
                          <DatePicker
                            selected={filters.dateRange.end}
                            onChange={(date) => handleDateChange(date, "end")}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            locale="es"
                            selectsEnd
                            startDate={filters.dateRange.start}
                            endDate={filters.dateRange.end}
                            minDate={filters.dateRange.start}
                            placeholderText="Seleccionar fecha"
                          />
                          <FontAwesomeIcon icon={faCalendar} />
                        </div>
                      </div>
                      <Button variant="link" className="remove-filter">
                        <FontAwesomeIcon icon={faMinus} />
                      </Button>
                    </div>
                  </div>

                  <div className="filter-row">
                    <div className="filter-field">
                      <Dropdown
                        show={showTemperatureDropdown}
                        onToggle={() =>
                          setShowTemperatureDropdown(!showTemperatureDropdown)
                        }
                      >
                        <Dropdown.Toggle
                          variant="light"
                          className="filter-dropdown-toggle"
                        >
                          Temperatura <FontAwesomeIcon icon={faChevronDown} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="temperature-dropdown">
                          {temperatureOptions.map((option) => (
                            <Dropdown.Item
                              key={option.value}
                              onClick={() => handleAddTemperature(option.value)}
                              className={
                                selectedTemperatures.includes(option.value)
                                  ? "selected"
                                  : ""
                              }
                            >
                              {option.label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <div className="selected-temperatures">
                        {selectedTemperatures.map((temp) => (
                          <div key={temp} className="temperature-tag">
                            {temp}
                            <button
                              type="button"
                              className="remove-tag"
                              onClick={() => handleRemoveTemperature(temp)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                      <Button variant="link" className="remove-filter">
                        <FontAwesomeIcon icon={faMinus} />
                      </Button>
                    </div>
                  </div>

                  <button className="add-filter-btn">+ Añadir filtro</button>
                </div>
              )}
            </div>

            <div className="search-container">
              <BiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar en registro de campañas"
                className="search-input"
              />
            </div>
          </div>

          <div className="campaigns-table-wrapper">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Fecha de creación</th>
                  <th>Canal</th>
                  <th>Estado Campaña</th>
                  <th>Estado Meta</th>
                  <th>Contactados</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      Cargando campañas...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                      {error}
                    </td>
                  </tr>
                ) : campaigns.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      No hay campañas disponibles
                    </td>
                  </tr>
                ) : (
                  getCurrentPageData().map((campaign) => (
                    <tr key={campaign.id}>
                      <td>{campaign.name}</td>
                      <td>{campaign.date}</td>
                      <td>
                        <div className="channels-cell">
                          {campaign.channels.includes("whatsapp") && (
                            <FaWhatsapp className="channel-icon whatsapp" />
                          )}
                          {campaign.channels.includes("phone") && (
                            <BsTelephone className="channel-icon phone" />
                          )}
                          {campaign.channels.length === 0 && (
                            <span style={{ color: '#6B7280', fontSize: '12px' }}>N/A</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: 12,
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            backgroundColor:
                              campaign.estadoCampaniaRaw === 'completado'
                                ? '#D1FAE5'
                                : campaign.estadoCampaniaRaw === 'programado'
                                  ? '#DBEAFE'
                                  : campaign.estadoCampaniaRaw === 'procesando'
                                    ? '#FEF3C7'
                                    : campaign.estadoCampaniaRaw === 'cancelado' || campaign.estadoCampaniaRaw === 'fallido'
                                      ? '#FEE2E2'
                                      : '#F3F4F6',
                            color:
                              campaign.estadoCampaniaRaw === 'completado'
                                ? '#065F46'
                                : campaign.estadoCampaniaRaw === 'programado'
                                  ? '#1E40AF'
                                  : campaign.estadoCampaniaRaw === 'procesando'
                                    ? '#92400E'
                                    : campaign.estadoCampaniaRaw === 'cancelado' || campaign.estadoCampaniaRaw === 'fallido'
                                      ? '#991B1B'
                                      : '#6B7280',
                          }}
                        >
                          {campaign.estadoCampania}
                        </Box>
                      </td>
                      <td>
                        {campaign.plantillaMetaStatus ? (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: 12,
                              fontWeight: 500,
                              backgroundColor:
                                campaign.plantillaMetaStatus === 'APPROVED'
                                  ? '#D1FAE5'
                                  : campaign.plantillaMetaStatus === 'PENDING'
                                    ? '#FEF3C7'
                                    : campaign.plantillaMetaStatus === 'REJECTED'
                                      ? '#FEE2E2'
                                      : '#E5E7EB',
                              color:
                                campaign.plantillaMetaStatus === 'APPROVED'
                                  ? '#065F46'
                                  : campaign.plantillaMetaStatus === 'PENDING'
                                    ? '#92400E'
                                    : campaign.plantillaMetaStatus === 'REJECTED'
                                      ? '#991B1B'
                                      : '#6B7280',
                            }}
                          >
                            {campaign.plantillaEstado}
                          </Box>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Sin plantilla</span>
                        )}
                      </td>
                      <td>{campaign.contactados}</td>
                      <td>
                        <div className="actions-cell">
                          <div className="switch-wrapper">
                            <Form.Check
                              type="switch"
                              id={`custom-switch-${campaign.id}`}
                              checked={campaign.active}
                              onChange={() => handleToggleActive(campaign.id)}
                              className="custom-switch"
                            />
                          </div>
                          <div className="dropdown-wrapper">
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                as="span"
                                variant="link"
                                className="icono-dropdown p-0"
                                style={{ cursor: 'pointer' }}
                              >
                                <RiMore2Fill className="text-muted" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu
                                container="body"
                                popperConfig={{
                                  modifiers: [
                                    {
                                      name: 'preventOverflow',
                                      options: {
                                        boundary: 'viewport',
                                      },
                                    },
                                    {
                                      name: 'flip',
                                      options: {
                                        fallbackPlacements: ['top-end', 'bottom-end'],
                                      },
                                    },
                                  ],
                                }}
                                className="dropdown-menu-end"
                              >
                                {/* Opción "Duplicar" - Ocultada por ahora */}
                                {/* <Dropdown.Item className="d-flex align-items-center">
                                  <RiFileCopyLine className="me-2" /> Duplicar
                                </Dropdown.Item> */}
                                {/* Botón Ver Detalles Plantilla */}
                                {campaign.plantillaData && (
                                  <>
                                    <Dropdown.Item
                                      className="d-flex align-items-center"
                                      onClick={() => {
                                        setSelectedPlantillaDetails(campaign.plantillaData);
                                        setShowPlantillaModal(true);
                                      }}
                                    >
                                      <RiEyeLine className="me-2" /> Ver Estado Meta
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                  </>
                                )}
                                {/* 👇 BOTÓN DE EJECUTAR (NUEVO) 👇 */}
                                {/*<Dropdown.Item 
                                  className="d-flex align-items-center text-primary"
                                  style={{ fontWeight: 'bold' }}
                                  onClick={() => handleExecute(campaign)}
                                >
                                  <RiSendPlaneLine className="me-2" /> Ejecutar Envío
                                </Dropdown.Item>
                                <Dropdown.Divider />*/}
                                {/* 👆 FIN BOTÓN EJECUTAR 👆 */}
                                <Dropdown.Item
                                  className="d-flex align-items-center"
                                  onClick={() => navigate(`/dashboard/automatizacion/campaigns/editar/${campaign.id}`)}
                                >
                                  <RiPencilLine className="me-2" /> Editar
                                </Dropdown.Item>
                                <Dropdown.Item
                                  className="d-flex align-items-center text-danger"
                                  onClick={() => handleDelete(campaign)}
                                >
                                  <RiDeleteBinLine className="me-2" /> Eliminar
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <div className="records-per-page">
              <span>Registros por página:</span>
              <Form.Select
                value={recordsPerPage}
                onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                className="records-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>
            <div className="pagination-controls">
              <Button
                variant="link"
                className="page-nav"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </Button>
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 &&
                    pageNumber <= currentPage + 1)
                ) {
                  return (
                    <Button
                      key={pageNumber}
                      variant="link"
                      className={`page-number ${pageNumber === currentPage ? "active" : ""
                        }`}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return (
                    <span key={pageNumber} className="page-ellipsis">
                      ...
                    </span>
                  );
                }
                return null;
              })}
              <Button
                variant="link"
                className="page-nav"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                &gt;
              </Button>
            </div>
          </div>
        </div>

        <ConfirmacionModal
          show={showDeleteModal}
          onHide={() => {
            if (!deleting) {
              setShowDeleteModal(false);
              setCampaignToDelete(null);
            }
          }}
          onConfirm={confirmDelete}
          title="Eliminar campaña"
          message="¿Estás seguro que deseas eliminar esta campaña? Esta acción no se puede deshacer."
          confirmButtonText="Eliminar"
          cancelButtonText="Cancelar"
          loading={deleting}
        />


        {/* Modal de Detalles de Plantilla Meta */}
        <Dialog
          open={showPlantillaModal}
          onClose={() => {
            setShowPlantillaModal(false);
            setSelectedPlantillaDetails(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: '18px' }}>
            Detalles de Plantilla Meta
          </DialogTitle>
          <DialogContent>
            {selectedPlantillaDetails && (
              <Box sx={{ pt: 1 }}>
                <Box sx={{ mb: 2 }}>
                  <strong>Nombre:</strong> {selectedPlantillaDetails.nombre || 'N/A'}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <strong>Estado en Meta:</strong>{' '}
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: 12,
                      fontWeight: 500,
                      marginLeft: '8px',
                      backgroundColor:
                        selectedPlantillaDetails.metaStatus === 'APPROVED'
                          ? '#D1FAE5'
                          : selectedPlantillaDetails.metaStatus === 'PENDING'
                            ? '#FEF3C7'
                            : selectedPlantillaDetails.metaStatus === 'REJECTED'
                              ? '#FEE2E2'
                              : '#E5E7EB',
                      color:
                        selectedPlantillaDetails.metaStatus === 'APPROVED'
                          ? '#065F46'
                          : selectedPlantillaDetails.metaStatus === 'PENDING'
                            ? '#92400E'
                            : selectedPlantillaDetails.metaStatus === 'REJECTED'
                              ? '#991B1B'
                              : '#6B7280',
                    }}
                  >
                    {selectedPlantillaDetails.metaStatus || 'LOCAL'}
                  </Box>
                </Box>
                {selectedPlantillaDetails.metaTemplateId && (
                  <Box sx={{ mb: 2 }}>
                    <strong>ID en Meta:</strong> {selectedPlantillaDetails.metaTemplateId}
                  </Box>
                )}
                {selectedPlantillaDetails.metaSyncedAt && (
                  <Box sx={{ mb: 2 }}>
                    <strong>Última sincronización:</strong>{' '}
                    {new Date(selectedPlantillaDetails.metaSyncedAt).toLocaleString('es-PE')}
                  </Box>
                )}
                {selectedPlantillaDetails.metaError && (
                  <Box sx={{ mb: 2, color: '#DC2626' }}>
                    <strong>Error de Meta:</strong> {selectedPlantillaDetails.metaError}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <MuiButton
              onClick={() => {
                setShowPlantillaModal(false);
                setSelectedPlantillaDetails(null);
              }}
              sx={{
                backgroundColor: '#6a8bff',
                color: '#fff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#5a7aef',
                },
              }}
            >
              Cerrar
            </MuiButton>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};
